import React, { FC, useEffect, useRef, useState }                                                                                                     from 'react';
import _                                                                                                                                              from 'underscore';
import moment                                                                                                                                         from 'moment';
import { Button, Form, Header, Icon, List, Menu, Modal, Segment, SemanticICONS, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from 'semantic-ui-react';
import { Formik, FormikProps }                                                                                                                        from 'formik';
import * as Yup                                                                                                                                       from 'yup';
import { useQuery }                                                                                                                                   from '@apollo/react-hooks';
import { Cell, Grid }                                                                                                                                 from 'styled-css-grid';

import { cloneWithoutReference, runIfFunction }                                                                                                                                    from '@src/util';
import { ProjectQueries, SystemAccountingQueries, UserQueries }                                                                                                                    from '@src/queries';
import { Align, CorvalProjectTemplate, CorvalProjectVersionMilestone, CorvalUIColor, Position, SemanticUIColor, SemanticUISize, Size, Uuid }                                       from '@src/types';
import { queriesHaveData, queriesLoading }                                                                                                                                         from '@src/store/_apolloUtil';
import { useCurrentUser, useCustomFormikHelpers, usePrefStates, useProject, useProjectMilestone, useSystemModals, useYupExtensions }                                               from '@src/hooks';
import { LangService, ProjectService, TimeService }                                                                                                                                from '@src/services';
import { CONSTANTS, CONTEXTS, ENTITY_ICONS, ORGANIZATIONAL_ROLES, projectTimelineFields, SYSTEM_TEMPLATE_IDS }                                                                     from '@src/constants';
import { StyledContainer }                                                                                                                                                         from '@src/views/containers';
import { QueryOpDialog }                                                                                                                                                           from '@src/views/components/from-query/QueryOpDialog';
import { BasicDialogContainer }                                                                                                                                                    from '@src/views/components/modals/BasicDialogContainer';
import { LoadingIndicator }                                                                                                                                                        from '@src/views/components/util/loading/LoadingIndicator';
import { MenuItemWithIconAndIndicator }                                                                                                                                            from '@src/views/components/menus/MenuItemWithIconAndIndicator';
import { UseIf }                                                                                                                                                                   from '@src/views/components/display/EqualValues';
import { MenuizedFocusContainerLayout }                                                                                                                                            from '@src/views/containers/MenuizedFocusContainerLayout';
import { FinePrintMessage }                                                                                                                                                        from '@src/views/components/util/messaging/FinePrintMessage';
import { EmptyCollectionMessage }                                                                                                                                                  from '@src/views/components/util/messaging/EmptyCollectionMessage';
import { DateField, FieldDescription, FieldWrapper, MultipleInputArrayField, OrganizationalMembersDropdown, OrganizationsDropdown, ProjectRoleDropdown, TextAreaField, TextField } from '@src/views/components/forms';
import { ItemWithIcon }                                                                                                                                                            from '@src/views/components/display/ItemWithIcon';
import { ItemWithNotifier }                                                                                                                                                        from '@src/views/components/display/ItemWithNotifier';
import { FieldLabel }                                                                                                                                                              from '@src/views/components/forms/formik/FieldLabel';
import { ProjectTimelineMilestone }                                                                                                                                                from '@src/views/components/project/ProjectTimelineMilestone';
import { ProjectMilestoneIcon }                                                                                                                                                    from '@src/views/components/project/ProjectMilestoneIcon';
import { ErrorBoundary }                                                                                                                                                           from '@src/views/components/util/errorHandling/ErrorBoundary';
import { BasicDialog }                                                                                                                                                             from '@src/views/components/modals/BasicDialog';
import { TextLink }                                                                                                                                                                from '@src/views/components/display/TextLink';
import { TemplateListingWithSearch }                                                                                                                                               from '@src/views/pages/projects/operations/templates/TemplateListingWithSearch';
import { ProgressionBar }                                                                                                                                                          from "@src/views/containers/ProgressionBar";


type SetupViewConfig = {
    menuLabel: string,
    menuIcon: SemanticICONS,
    onMenuClick: () => void,
    hasOptions: boolean,
    minOptionWidth: string,
    maxOptionWidth: string,
    previousView: SETUP_VIEWS,
    nextView: SETUP_VIEWS,
}

enum SETUP_VIEWS {
    TEMPLATE_SELECTION = 'TEMPLATE_SELECTION',
    PROJECT_DETAILS    = 'PROJECT_DETAILS',
    PROJECT_TIMELINE   = 'PROJECT_TIMELINE',
    PROJECT_TEAM       = 'PROJECT_TEAM',
}

interface NewProjectFromTemplateFormValues {
    templateId: null | string,
    organizationId: string,
    name: string,
    assetName: string,
    assetIndication: string,
    startDate: null | string,
    projectMembers: any[],
}

const initialValues: NewProjectFromTemplateFormValues = {
    templateId:      '',
    organizationId:  '',
    name:            '',
    assetName:       '',
    assetIndication: '',
    startDate:       '',
    projectMembers:  [],
};

interface CreateProjectDialogProps {
    onComplete?: ( data: any ) => void,
}

const CreateProjectDialogDefaultProps = {
    onComplete: null,
} satisfies Partial<CreateProjectDialogProps>;

function CreateProjectDialog( _props: CreateProjectDialogProps ){

    const props = { ...CreateProjectDialogDefaultProps, ..._props };

    const { currentUser, isSystemAdmin, hasOrganizationalRole }       = useCurrentUser(),
          [ setupView, setSetupView ]                                 = useState<SETUP_VIEWS>( SETUP_VIEWS.PROJECT_DETAILS ),
          [ projectListing, setProjectListing ]                       = useState( [] ),
          [ templateListing, setTemplateListing ]                     = useState( [] ),
          [ selectedTemplate, setSelectedTemplate ]                   = useState( null ),
          [ createdProjectId, setCreatedProjectId ]                   = useState( null ),
          [ showEndTransactionDisplay, setShowEndTransactionDisplay ] = useState( false ),
          [ , prefActions, prefEnums ]                                = usePrefStates(),
          { openProject }                                             = useProject(),
          { getTimelineFieldIconProperties }                          = useProjectMilestone(),
          { includesErrorsIn }                                        = useCustomFormikHelpers(),
          { addStringIsDateFormatTest }                               = useYupExtensions(),
          modalActions                                                = useSystemModals(),
          formikRef                                                   = useRef<FormikProps<NewProjectFromTemplateFormValues>>( null );

    const setupViewConfigs: Record<SETUP_VIEWS, SetupViewConfig> = {

        [ SETUP_VIEWS.PROJECT_DETAILS ]: {
            menuLabel:      'Details',
            menuIcon:       ENTITY_ICONS.PROJECTS.ENTITY,
            onMenuClick:    () => {
                setSetupView( SETUP_VIEWS.PROJECT_DETAILS );
            },
            hasOptions:     false,
            minOptionWidth: '150px',
            maxOptionWidth: 'auto',
            previousView:   null,
            nextView:       SETUP_VIEWS.TEMPLATE_SELECTION,
        },

        [ SETUP_VIEWS.TEMPLATE_SELECTION ]: {
            menuLabel:      'Templates',
            menuIcon:       ENTITY_ICONS.SYSTEM.SETTINGS,
            onMenuClick:    () => {
                setSetupView( SETUP_VIEWS.TEMPLATE_SELECTION );
            },
            hasOptions:     false,
            minOptionWidth: '150px',
            maxOptionWidth: 'auto',
            previousView:   SETUP_VIEWS.PROJECT_DETAILS,
            nextView:       SETUP_VIEWS.PROJECT_TIMELINE,
        },

        [ SETUP_VIEWS.PROJECT_TIMELINE ]: {
            menuLabel:      'Timeline',
            menuIcon:       ENTITY_ICONS.MILESTONES.ENTITY,
            onMenuClick:    () => {
                setSetupView( SETUP_VIEWS.PROJECT_TIMELINE );
            },
            hasOptions:     false,
            minOptionWidth: '150px',
            maxOptionWidth: 'auto',
            previousView:   SETUP_VIEWS.TEMPLATE_SELECTION,
            nextView:       SETUP_VIEWS.PROJECT_TEAM,
        },

        [ SETUP_VIEWS.PROJECT_TEAM ]: {
            menuLabel:      'Team',
            menuIcon:       ENTITY_ICONS.USER.ENTITY,
            onMenuClick:    () => {
                setSetupView( SETUP_VIEWS.PROJECT_TEAM );
            },
            hasOptions:     false,
            minOptionWidth: '150px',
            maxOptionWidth: 'auto',
            previousView:   SETUP_VIEWS.PROJECT_TIMELINE,
            nextView:       null,
        },
    };

    const getCurrentUserQuery     = UserQueries.queries.GET_CURRENT_USER(),
          getProjectListingQuery  = SystemAccountingQueries.queries.GET_PROJECTS(),
          getTemplateListingQuery = SystemAccountingQueries.queries.GET_PROJECT_TEMPLATES( `
                id
                category
                description
                icon
                isArchived
                name
                organizations {
                    organizationId
                }
                tags {
                    tag
                }
                projectVersion {
                
                    ${ ProjectQueries.fragments.PROJECTS.TIMELINE_BOUNDARY_FIELDS() }
                    
                    projectVersionMilestones {
                        ${ ProjectQueries.fragments.PROJECTS.MILESTONE_FIELDS }
                    }
                }`
          );

    const getProjectListingReq   = useQuery( getProjectListingQuery, { fetchPolicy: 'no-cache' } ),
          getTemplateListingReq  = useQuery( getTemplateListingQuery, { fetchPolicy: 'no-cache' } ),
          getCurrentUserQueryReq = useQuery( getCurrentUserQuery, { fetchPolicy: 'no-cache' } );

    const isLoadingListings = ( queriesLoading( [ getProjectListingReq, getTemplateListingReq ] ) );

    useEffect( () => {}, [ selectedTemplate ] );

    useEffect( () => {

        if( !queriesLoading( getProjectListingReq ) &&
            queriesHaveData( getProjectListingReq ) ){

            setProjectListing( getProjectListingReq.data?.projects.map( p => p.name ) ?? [] );
        }

    }, [ getProjectListingReq ] );

    useEffect( () => {

        console.log( getTemplateListingReq?.data?.me?.projectTemplates ?? null );

        if( !queriesLoading( [ getCurrentUserQueryReq, getTemplateListingReq ] ) &&
            queriesHaveData( [ getCurrentUserQueryReq, getTemplateListingReq ] ) ){

            const systemTemplates = [
                {
                    id:            SYSTEM_TEMPLATE_IDS.EMPTY_PROJECT,
                    name:          'Corval - Empty Project',
                    description:   'No template used. This will create a blank project.',
                    category:      'Corval System',
                    icon:          'globe',
                    organizations: [],
                    tags:          [ 'corval', 'system', 'empty' ],
                },
                {
                    id:            SYSTEM_TEMPLATE_IDS.FULL_PROJECT_3Y,
                    name:          'Corval - Full Content - 3 Years',
                    description:   'No template used. This will create a project with all available Corval content loaded in and milestones setup within a 3 year timeline.',
                    category:      'Corval System',
                    icon:          'globe',
                    organizations: [],
                    tags:          [ 'corval', 'system', 'basic' ],
                    isArchived:    true,
                },
                {
                    id:            SYSTEM_TEMPLATE_IDS.FULL_PROJECT_5Y,
                    name:          'Corval - Full Content - 5 Years',
                    description:   'No template used. This will create a project with all available Corval content loaded in and milestones setup within a 5 year timeline.',
                    category:      'Corval System',
                    icon:          'globe',
                    organizations: [],
                    tags:          [ 'corval', 'system', 'basic' ],
                    isArchived:    true,
                }
            ];

            const userOrgs = _.pluck( getCurrentUserQueryReq.data?.me?.organizations ?? [], 'organizationId' );

            // filter out orgs that are not in the user's org memberships
            const templateListings = ( getTemplateListingReq.data?.me?.projectTemplates ?? [] )
                .filter( ( template: CorvalProjectTemplate ) => {

                    // For templates that are archived, never include
                    return !template.isArchived;

                } )
                .filter( ( template: CorvalProjectTemplate ) => {

                    // For templates that have no orgs attached, include for all users
                    if( _.isEmpty( template.organizations ) ) return true;

                    // Otherwise, match on org ids available to specific user
                    const templateOrgs = _.pluck( template.organizations, 'organizationId' );

                    return _.intersection( templateOrgs, userOrgs ).length > 0;
                } )
                .map( ( template: CorvalProjectTemplate ) => ( {
                    ...template,
                    tags: _.pluck( template.tags, 'tag' ),
                } ) );

            const fullSortedListing = [
                ..._.sortBy( systemTemplates, 'name' ),
                ..._.sortBy( templateListings, 'name' ),
            ];

            setTemplateListing( fullSortedListing );

        }

    }, [ getTemplateListingReq, getCurrentUserQueryReq ] );


    addStringIsDateFormatTest();

    const validationSchema = Yup.object().shape( {

        name:            Yup.string().min( 3, 'A project must have a name with at least 3 characters.' ).required( 'A project must have a name.' ),
        assetName:       Yup.string().min( 3, 'A project must have a name with at least 3 characters.' ).required( 'The asset name must be specified.' ),
        assetIndication: Yup.string().min( 20, 'A project must have a name with at least 20 characters.' ).required( 'The asset indication cannot be empty.' ),
        templateId:      Yup.string().required( 'A template scheme must be chosen.' ),
        startDate:       Yup.string().isDateFormat().required( 'A start date must be specified.' ),
        organizationId:  Yup.string()
                            .required( 'An organization must be chosen.' )
                            .test(
                                'user-has-org-role',
                                `Your role for this organization needs to be admin or higher to create a project.`,
                                value => {
                                    return ( isSystemAdmin() || hasOrganizationalRole( props, ORGANIZATIONAL_ROLES.ADMIN ) );
                                }
                            ),
        projectMembers:  Yup.array()
                            .of(
                                Yup.object( {
                                    accountId:   Yup.string().required( 'Please select a team member.' ),
                                    projectRole: Yup.string().required( 'Please select a role.' ),
                                } )
                            )
                            .min( 1, 'At least one team member must be included on the project.' )
                            .test(
                                'admin-assigned',
                                `Teams must contain at least one "${ LangService.getTermFromDictionary( CONSTANTS.PRODUCT.PROJECT_ROLE_INFO.ADMIN.LABEL ) }" role`,
                                value => {
                                    return ( _.filter( value, member => member[ 'projectRole' ] === CONSTANTS.PRODUCT.PROJECT_ROLES.ADMIN ).length > 0 );
                                }
                            ),

    } );

    const mutationOperation = SystemAccountingQueries.mutations.CREATE_PROJECT_FROM_TEMPLATE();

    const getMutationGraphArgs = () => {

        const { values } = formikRef.current;

        return ( {
            variables: {
                input: {
                    projectName:       values.name,
                    projectTemplateId: values.templateId,
                    assetName:         values.assetName,
                    indicationName:    values.assetIndication,
                    startDate:         TimeService.displayDateToLocalDate( values.startDate ),
                    organizationId:    values.organizationId,
                    projectMembers:    values.projectMembers,
                }
            },

            awaitRefetchQueries: true,

            refetchQueries: [
                { query: getProjectListingQuery, variables: {} },
                { query: getTemplateListingQuery, variables: {} },
                { query: getCurrentUserQuery, variables: {} },
            ],
        } );

    };

    const getSelectedTemplate = ( templateId: string ) => {

        return _.findWhere( templateListing, { id: templateId } ) ?? null;

    };

    const hasSelectedTemplate = () => {

        const template = getSelectedTemplate( selectedTemplate );

        return _.isObject( template );

    };

    const isSystemTemplate = ( templateId: any ) => {

        return [
            SYSTEM_TEMPLATE_IDS.EMPTY_PROJECT,
            SYSTEM_TEMPLATE_IDS.FULL_PROJECT_3Y,
            SYSTEM_TEMPLATE_IDS.FULL_PROJECT_5Y
        ].includes( templateId );
    };

    const getSelectedTemplateMilestones = ( projectStartDate: string | null = null ) => {

        if( !hasSelectedTemplate() ) return [];

        const _selectedTemplate = getSelectedTemplate( selectedTemplate ),
              _isSystemTemplate = isSystemTemplate( _selectedTemplate.id );

        if( _isSystemTemplate ) return [];

        const originalTemplateMilestones = ( _selectedTemplate?.projectVersion.projectVersionMilestones ) ?? [],
              templateMilestones         = cloneWithoutReference( originalTemplateMilestones );

        if( projectStartDate ){

            // Get the minimum date to base offset on
            const minDate = TimeService.localDateToMoment( _selectedTemplate.projectVersion.mapLowerBound );

            try{

                // Get the day differential between the project start date and the minimum date
                const dayDiff = TimeService.getDayDifferential( TimeService.displayDateToMoment( projectStartDate ), minDate, false );

                // Adjust the template milestones to reflect the project start date + offset in days
                templateMilestones.map( milestone => {
                    milestone.milestoneDate = TimeService.momentToLocalDate( TimeService.localDateToMoment( milestone.milestoneDate ).add( dayDiff, 'days' ) );
                } );

            } catch( err ) {

                throw new Error( err );
            }
        }

        // Sort the milestone items by (new) milestone date
        return _.sortBy( templateMilestones, m => m.milestoneDate );
    };

    const handleOnComplete = ( data ) => {

        const newProject = data.createProjectFromTemplate;

        runIfFunction( props.onComplete, {
            projectId: newProject.id
        } );

        // Record the created project ID
        setCreatedProjectId( newProject.id );

        // Show next steps modal
        setShowEndTransactionDisplay( true );
    };

    const handleNavigateToNewProject = () => {

        if( createdProjectId ){

            // Add newly created project to the current visibility settings
            const prefKey                          = prefEnums.GLOBAL_ACTIVE_PROJECT_VISIBILITY,
                  visibilityState                  = prefActions.get( prefKey ),
                  updatedGloballyVisibleProjectIds = _.union( prefActions.get( prefKey ), [ createdProjectId ] );

            if( !_.isEmpty( visibilityState ) ){
                prefActions.update( prefKey, updatedGloballyVisibleProjectIds );
            }

            //
            setTimeout( () => {

                openProject( createdProjectId );

                modalActions.toggle( false );

            }, 200 );
        }
    };

    const handleCloseCreateProjectDialog = () => {

        modalActions.toggle( false );
    };

    const handleResetForm = () => {

        formikRef.current.resetForm();

        setShowEndTransactionDisplay( false );

        setSetupView( SETUP_VIEWS.PROJECT_DETAILS );
    };

    const handleNextStep = () => {

        if( setupViewConfigs[ setupView ].nextView ){
            setSetupView( setupViewConfigs[ setupView ].nextView );
        }
    }

    const handleBackStep = () => {

        if( setupViewConfigs[ setupView ].previousView ){
            setSetupView( setupViewConfigs[ setupView ].previousView );
        }
    }

    return (

        <Formik<NewProjectFromTemplateFormValues>
            innerRef={ formikRef }
            initialValues={ initialValues }
            isInitialValid={ false }
            enableReinitialize={ true }
            validateOnChange={ true }
            validateOnMount={ true }
            validationSchema={ validationSchema }
            onSubmit={ null }>


            { formik => {

                const validationHash: Record<SETUP_VIEWS, Array<keyof NewProjectFromTemplateFormValues>> = {
                    [ SETUP_VIEWS.PROJECT_DETAILS ]:    [ 'name', 'assetName', 'assetIndication' ],
                    [ SETUP_VIEWS.TEMPLATE_SELECTION ]: [ 'templateId' ],
                    [ SETUP_VIEWS.PROJECT_TIMELINE ]:   [ 'startDate' ],
                    [ SETUP_VIEWS.PROJECT_TEAM ]:       [ 'organizationId', 'projectMembers' ],
                };

                let sectionState: Record<SETUP_VIEWS, {
                    inputsTouched: Array<keyof NewProjectFromTemplateFormValues>,
                    inputsHaveErrors: boolean,
                    inputsHaveNulledErrors: boolean,
                    sectionErrors: Record<keyof NewProjectFromTemplateFormValues, string>,
                    sectionHasInputsTouched: boolean,
                    sectionIsComplete: boolean,
                    order: number,
                }> | object = {};

                _.pairs( setupViewConfigs ).map( ( [ type, config ], index ) => {
                    const validationInputs        = validationHash[ type ] ?? [],
                          inputsTouched           = _.keys( formik.touched ).filter( inputKey => ( validationInputs as string[] ).includes( inputKey ) ),
                          inputsHaveErrors        = includesErrorsIn( formik, ...validationInputs ),
                          inputsHaveNulledErrors  = _.chain( formik.errors ).pick( validationInputs ).pairs().value().some( ( [ errorEntry, errorDesc ] ) => _.isNull( errorDesc ) ),
                          sectionHasInputsTouched = !_.isEmpty( inputsTouched ),
                          sectionIsComplete       = sectionHasInputsTouched && !inputsHaveErrors && !inputsHaveNulledErrors;

                    sectionState[ type ] = {
                        order:         index,
                        sectionErrors: _.pick( formik.errors, ...validationInputs ) as Record<keyof NewProjectFromTemplateFormValues, string>,
                        inputsTouched,
                        sectionHasInputsTouched,
                        inputsHaveErrors,
                        inputsHaveNulledErrors,
                        sectionIsComplete,
                    };
                } );

                const generateProgressBar = ( setupView: SETUP_VIEWS ) => {

                    const { sectionIsComplete, order } = sectionState[ setupView ],
                          stepNumber                   = order + 1,
                          stepTotalCount               = _.keys( sectionState ).length,
                          isFirstStep                  = ( order === 0 ),
                          isLastStep                   = ( order === ( stepTotalCount - 1 ) );

                    return (
                        <ProgressionBar
                            showLeftComponent={ !isFirstStep }
                            showRightComponent={ !isLastStep }

                            rightComponentReady={ sectionIsComplete }
                            rightComponentDisabled={ !sectionIsComplete }

                            onLeftComponentClick={ handleBackStep }
                            onRightComponentClick={ handleNextStep }

                            centerComponent={ `Step ${ stepNumber } / ${ stepTotalCount }` }
                        />
                    );
                }

                return (

                    <>
                        {/* Next Steps Dialog (second modal) */ }
                        <Modal open={ showEndTransactionDisplay } size="tiny">

                            <BasicDialog
                                operationIsDangerous={ false }
                                hasSystemContext={ false }
                                onComplete={ handleNavigateToNewProject }
                                onClose={ handleCloseCreateProjectDialog }
                                submitLabel={ 'Go to New Project' }
                                cancelLabel={ 'Close' }
                                className={ 'no-padding' }>

                                <BasicDialogContainer title={ 'Next Steps' }>

                                    <p>The project <strong>"{ formik.values.name }"</strong> has been created successfully.</p>
                                    <TextLink onClick={ handleResetForm }>Create Another Project</TextLink>

                                </BasicDialogContainer>

                            </BasicDialog>

                        </Modal>

                        {/* Create Project Dialog (first modal) */ }
                        <QueryOpDialog
                            { ...props }
                            autoClose={ setupView === SETUP_VIEWS.PROJECT_DETAILS }
                            operationIsHidden={ ( isLoadingListings || !formik.isValid ) }
                            operationDisabled={ isLoadingListings || !formik.isValid }
                            mutation={ mutationOperation }
                            graphArgs={ getMutationGraphArgs }
                            onComplete={ handleOnComplete }
                            submitLabel={ 'Create Project' }
                            cancelLabel={ 'Cancel' }
                            onClose={ handleCloseCreateProjectDialog }
                            className={ 'no-padding' }
                            overflow={ true }
                            scrolling={ false }
                            alignControls={ Align.CENTER }>

                            <Form>

                                <BasicDialogContainer
                                    title={ 'Create New Project' }
                                    contentMinHeight={ `75dvh` }
                                    contentMaxHeight={ `75dvh` }
                                    contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }
                                    containerStyles={ { position: 'absolute', inset: `20px 20px 50px 20px` } }
                                    overflow={ true }
                                    scrolling={ false }
                                    roundedTop={ true }>

                                    <ErrorBoundary>

                                        <UseIf x={ isLoadingListings } eq={ true }>

                                            <StyledContainer padding={ Size.LG }>
                                                <LoadingIndicator label={ 'Loading...' } isActive={ isLoadingListings } inverted/>
                                            </StyledContainer>

                                        </UseIf>
                                        <UseIf x={ isLoadingListings } eq={ false }>

                                            <StyledContainer className={ 'padding-sm-left' }>
                                                <ItemWithIcon middleAlign={ true } iconComponent={ <Icon name={ ENTITY_ICONS.PROJECTS.ENTITY } color={ SemanticUIColor.PURPLE } size={ SemanticUISize.LARGE } fixed/> }>
                                                    <Header as={ 'h3' }>{ _.isEmpty( formik.values.name ) ? 'New Project' : formik.values.name }</Header>
                                                </ItemWithIcon>
                                            </StyledContainer>

                                            <MenuizedFocusContainerLayout
                                                maxMenuWidth={ '180px' }
                                                minOptionWidth={ '200px' }
                                                maxOptionWidth={ '200px' }
                                                minContainerHeight={ '400px' }
                                                menuContent={ (

                                                    <Menu fluid vertical size={ 'small' } key={ `${ JSON.stringify( formik.values ) }` }>

                                                        { _.pairs( setupViewConfigs ).map( ( [ type, config ] ) => {

                                                            const { sectionHasInputsTouched, sectionIsComplete } = sectionState[ type ];

                                                            return (

                                                                <ItemWithNotifier
                                                                    key={ type }
                                                                    notifyContext={ ( !sectionHasInputsTouched || sectionIsComplete ) ? null : CONTEXTS.ERROR }
                                                                    notifyStyle={ 'dot' }
                                                                    notificationPosition={ Align.RIGHT }
                                                                    notificationContent={ 'This section is missing information or has issues.' }
                                                                    notificationXOffset={ -5 }
                                                                    notificationYOffset={ -3 }
                                                                    pulseNotifier={ true }
                                                                    content={

                                                                        <MenuItemWithIconAndIndicator
                                                                            key={ type }
                                                                            menuIcon={ sectionIsComplete ? 'check circle' : config.menuIcon }
                                                                            menuIconColor={ sectionIsComplete ? 'green' : ( setupView !== type ) ? 'grey' : null }
                                                                            listProps={ {
                                                                                color:    sectionIsComplete ? 'green' : null,
                                                                                disabled: !sectionIsComplete
                                                                            } }
                                                                            onClick={ config.onMenuClick }
                                                                            withIndicator={ false }
                                                                            isActive={ setupView === type }>

                                                                            { config.menuLabel }

                                                                        </MenuItemWithIconAndIndicator>
                                                                    }
                                                                />

                                                            );
                                                        } ) }

                                                    </Menu>

                                                ) }
                                                viewContent={ (

                                                    <>
                                                        <Segment raised style={ { height: `calc(100% - 80px )` } }>

                                                            {/* DETAILS SECTION */ }
                                                            <UseIf x={ setupView } eq={ SETUP_VIEWS.PROJECT_DETAILS }>

                                                                <TextField
                                                                    type="text"
                                                                    size={ 'big' }
                                                                    label={ 'Project Name' }
                                                                    name={ 'name' }
                                                                    placeholder="eg: My Project"
                                                                    includeErrorMessage={ false }
                                                                    loading={ isLoadingListings }/>

                                                                <TextField
                                                                    type="text"
                                                                    label={ 'Asset Name' }
                                                                    descriptionText={ 'The name your team uses to describe the asset.' }
                                                                    name={ 'assetName' }
                                                                    placeholder="eg: CARTENZA-PFB"
                                                                    includeErrorMessage={ false }
                                                                    loading={ isLoadingListings }/>


                                                                <TextAreaField
                                                                    rows={ 8 }
                                                                    label={ 'Asset Indication' }
                                                                    descriptionText={ 'The indication of the asset refers to the use of that asset for treating a particular problem, such as a disease.' }
                                                                    name={ 'assetIndication' }
                                                                    includeErrorMessage={ false }
                                                                    disabled={ isLoadingListings }/>

                                                            </UseIf>

                                                            {/* TEMPLATE SECTION */ }
                                                            <UseIf x={ setupView } eq={ SETUP_VIEWS.TEMPLATE_SELECTION }>

                                                                <UseIf x={ isLoadingListings } eq={ false }>

                                                                    <TemplateListingWithSearch
                                                                        items={ templateListing }
                                                                        selectable={ true }
                                                                        selectedItemId={ formik.values.templateId }
                                                                        containerClasses={ 'overflow-content--y' }
                                                                        containerStyles={ { position: 'absolute', inset: 0 } }
                                                                        onListItemClicked={ templateId => {
                                                                            formik.setFieldTouched( 'templateId', true );
                                                                            formik.setFieldValue( 'templateId', templateId, true );
                                                                            setSelectedTemplate( templateId );
                                                                        } }
                                                                    />

                                                                </UseIf>

                                                            </UseIf>

                                                            {/* TIMELINE SECTION */ }
                                                            <UseIf x={ setupView } eq={ SETUP_VIEWS.PROJECT_TIMELINE }>

                                                                <Grid columns={ `1fr` } rows={ `auto 20px auto 1fr` } style={ { height: '100%' } }>
                                                                    <Cell>

                                                                        <DateField
                                                                            fluid
                                                                            clearable
                                                                            name={ 'startDate' }
                                                                            label={ 'Start Date' }
                                                                            descriptionText={ 'The staring date for this project. If this project was based on a template, the accompanying milestones will be updated to reflect this starting date.' }
                                                                            style={ { maxWidth: '50%' } }
                                                                            initialDate={ moment() }
                                                                            onAfterChange={ ( value, data, e ) => {} }
                                                                            popupPosition={ 'bottom right' }
                                                                            includeErrorMessage={ false }
                                                                        />

                                                                    </Cell>

                                                                    <Cell></Cell>

                                                                    <Cell>

                                                                        <FieldWrapper style={ { height: '100%' } }>
                                                                            <FieldLabel>{ hasSelectedTemplate() ? `Template` : `Project` } Milestones</FieldLabel>
                                                                            { hasSelectedTemplate()
                                                                              ? <FieldDescription>Commercialization milestones that originate from the selected template, adjusted to this project's start date. Milestones can be updated once the project is created.</FieldDescription>
                                                                              : <FinePrintMessage>Commercialization milestones will be generated by Corval. Milestones can be updated once the project is created</FinePrintMessage>
                                                                            }
                                                                        </FieldWrapper>

                                                                    </Cell>
                                                                    <Cell className={ 'position--relative overflow-content--y' }>

                                                                        <StyledContainer position={ Position.ABSOLUTE } style={ { inset: 0 } }>

                                                                            <Table style={ { borderTop: 'none' } }>
                                                                                <TableHeader className={ 'position--sticky z-index--100 position--top' }>
                                                                                    <TableRow>
                                                                                        <TableHeaderCell style={ { borderTop: `1px solid var(--color-lightGrey)`, borderRadius: '5px 0 0 0' } }>Milestone</TableHeaderCell>
                                                                                        <TableHeaderCell
                                                                                            style={ { borderTop: `1px solid var(--color-lightGrey)`, borderRadius: '0 5px 0 0' } }>{ hasSelectedTemplate() && !isSystemTemplate( selectedTemplate ) ? 'Projected Timing' : 'Description' }</TableHeaderCell>
                                                                                    </TableRow>
                                                                                </TableHeader>
                                                                                <TableBody>

                                                                                    {/* SYSTEM MILESTONES */ }
                                                                                    <UseIf x={ !hasSelectedTemplate() || isSystemTemplate( selectedTemplate ) } eq={ true }>

                                                                                        <EmptyCollectionMessage
                                                                                            icon={ ENTITY_ICONS.MILESTONES.ENTITY }
                                                                                            header={ 'System milestones planned.' }
                                                                                            content={ 'This template will derive its milestones from the system and copy them into your project.' }/>

                                                                                    </UseIf>

                                                                                    {/* TEMPLATE / CUSTOM MILESTONES */ }
                                                                                    <UseIf x={ hasSelectedTemplate() && !isSystemTemplate( selectedTemplate ) } eq={ true }>

                                                                                        {/* MILESTONES PRESENT */ }
                                                                                        <UseIf x={ !_.isEmpty( getSelectedTemplateMilestones( formik.values.startDate ) ) } eq={ true }>

                                                                                            { getSelectedTemplateMilestones( formik.values.startDate ).map( ( milestone: CorvalProjectVersionMilestone ) => {

                                                                                                const { iconColor, iconName } = getTimelineFieldIconProperties( milestone.iconName, milestone.colorValue )

                                                                                                return (
                                                                                                    <TableRow key={ milestone.id }>
                                                                                                        <TableCell className={ 'text--sm text--no-wrap' }>
                                                                                                            <><ProjectMilestoneIcon
                                                                                                                label={ milestone.name }
                                                                                                                desc={ milestone.description }
                                                                                                                icon={ { color: iconColor as SemanticUIColor } }
                                                                                                                date={ TimeService.localDateToDisplayDate(milestone.milestoneDate) }
                                                                                                            /> { milestone.name }</>
                                                                                                        </TableCell>
                                                                                                        <TableCell className={ 'text--sm' }>{ TimeService.localDateToDisplayDate( milestone.milestoneDate ) }</TableCell>
                                                                                                    </TableRow>
                                                                                                );

                                                                                            } ) }

                                                                                        </UseIf>

                                                                                        {/* MILESTONES NOT FOUND */ }
                                                                                        <UseIf x={ _.isEmpty( getSelectedTemplateMilestones( 'all', formik.values.startDate ) ) } eq={ true }>

                                                                                            <TableRow>
                                                                                                <TableCell colSpan={ 2 }>

                                                                                                    <EmptyCollectionMessage
                                                                                                        icon={ ENTITY_ICONS.MILESTONES.ENTITY }
                                                                                                        header={ 'No milestones found.' }
                                                                                                        content={ 'This template either has no milestones to use, or has not reported them correctly within the template system.' }/>

                                                                                                </TableCell>
                                                                                            </TableRow>

                                                                                        </UseIf>

                                                                                    </UseIf>

                                                                                </TableBody>

                                                                            </Table>

                                                                        </StyledContainer>

                                                                    </Cell>

                                                                </Grid>

                                                            </UseIf>

                                                            {/* TEAM SECTION */ }
                                                            <UseIf x={ setupView } eq={ SETUP_VIEWS.PROJECT_TEAM }>

                                                                <Grid columns={ `1fr` } rows={ `auto 20px 1fr` } style={ { height: '100%' } }>
                                                                    <Cell className={ 'position--relative z-index--200' }>

                                                                        <OrganizationsDropdown
                                                                            name={ 'organizationId' }
                                                                            label={ 'Organization' }
                                                                            descriptionText={ 'The organization this project will be created for.' }
                                                                            organizationIds={ _.pluck( currentUser.organizations, 'organizationId' )
                                                                                               .filter( organizationId => {

                                                                                                   const isAdminOfOrg   = hasOrganizationalRole( organizationId, ORGANIZATIONAL_ROLES.ADMIN ),
                                                                                                         availableOrgs  = _.pluck( getSelectedTemplate( selectedTemplate )?.organizations, 'organizationId' ) ?? [],
                                                                                                         allOrgsAllowed = _.isEmpty( availableOrgs ),
                                                                                                         isTemplateOrg  = hasSelectedTemplate() && ( allOrgsAllowed || availableOrgs.includes( organizationId ) );

                                                                                                   return isAdminOfOrg && ( !hasSelectedTemplate() || isTemplateOrg );

                                                                                               } ) }
                                                                            includeErrorMessage={ false }
                                                                            onChange={ ( value ) => {
                                                                                formik.setFieldValue( 'projectMembers', [], true );
                                                                            } }
                                                                        />

                                                                    </Cell>

                                                                    <Cell></Cell>

                                                                    <Cell className={ 'position--relative overflow-content--y' }>

                                                                        <StyledContainer position={ Position.ABSOLUTE } style={ { inset: 0 } }>

                                                                            { !_.isEmpty( currentUser.organizations ) ? (

                                                                                <MultipleInputArrayField<{ accountId: Uuid, projectRole: string }>
                                                                                    name={ 'projectMembers' }
                                                                                    label={ 'Project Team Members' }
                                                                                    addLabel={ 'Add Member' }
                                                                                    removeDisableFirstOnly={ false }
                                                                                    addDisabled={ !formik.values.organizationId }
                                                                                    objectFactory={ () => ( {
                                                                                        accountId:   '',
                                                                                        projectRole: ''
                                                                                    } ) }
                                                                                    headerContainerClasses={ [ 'position--sticky position--top z-index--100 bg--colored-white padding-sm margin-xs-bottom' ] }
                                                                                    renderField={ ( item, index ) => {

                                                                                        return (
                                                                                            <Grid columns={ 2 }>

                                                                                                <Cell>

                                                                                                    <OrganizationalMembersDropdown
                                                                                                        compact
                                                                                                        search
                                                                                                        id={ `org-${ formik.values.organizationId }` }
                                                                                                        organizationId={ formik.values.organizationId }
                                                                                                        omit={ _.without( _.pluck( formik.values.projectMembers, 'accountId' ), item.accountId ) }
                                                                                                        name={ `projectMembers[${ index }].accountId` }
                                                                                                        placeholder="Choose..."
                                                                                                        includeErrorMessage={ false }
                                                                                                        loading={ isLoadingListings }
                                                                                                    />

                                                                                                </Cell>
                                                                                                <Cell>

                                                                                                    <ProjectRoleDropdown
                                                                                                        compact
                                                                                                        name={ `projectMembers[${ index }].projectRole` }
                                                                                                        placeholder="Choose..."
                                                                                                        includeErrorMessage={ false }
                                                                                                        loading={ isLoadingListings }
                                                                                                    />

                                                                                                </Cell>

                                                                                            </Grid>

                                                                                        );
                                                                                    } }/>
                                                                            ) : (

                                                                                  <EmptyCollectionMessage icon="user" header="Organization has no members" content="Try adding an item to see them in a list."/>

                                                                              ) }

                                                                        </StyledContainer>

                                                                    </Cell>

                                                                </Grid>

                                                            </UseIf>

                                                        </Segment>

                                                        { generateProgressBar( setupView ) }

                                                    </>

                                                ) }
                                                optionsContent={ (

                                                    <>
                                                        <UseIf x={ setupView } eq={ SETUP_VIEWS.PROJECT_DETAILS }>
                                                            <Header as={ 'h5' }><Icon name={ setupViewConfigs[ setupView ].menuIcon } color={ SemanticUIColor.TEAL }/> Project Details</Header>
                                                            <FinePrintMessage>Describe your project for the team.</FinePrintMessage>
                                                        </UseIf>

                                                        <UseIf x={ setupView } eq={ SETUP_VIEWS.TEMPLATE_SELECTION }>
                                                            <Header as={ 'h5' }><Icon name={ setupViewConfigs[ setupView ].menuIcon } color={ SemanticUIColor.TEAL }/> Template Selection</Header>
                                                            <FinePrintMessage>Select a template to start content with, or no template for a basic project.</FinePrintMessage>
                                                        </UseIf>

                                                        <UseIf x={ setupView } eq={ SETUP_VIEWS.PROJECT_TIMELINE }>
                                                            <Header as={ 'h5' }><Icon name={ setupViewConfigs[ setupView ].menuIcon } color={ SemanticUIColor.TEAL }/> Timeline Setup</Header>
                                                            <FinePrintMessage>Specify when your project should begin and end. When setting up a project, Corval will automatically setup basic milestones for you. When selecting a template, additional milestones will be listed for
                                                                reference.</FinePrintMessage>
                                                        </UseIf>

                                                        <UseIf x={ setupView } eq={ SETUP_VIEWS.PROJECT_TEAM }>
                                                            <Header as={ 'h5' }><Icon name={ setupViewConfigs[ setupView ].menuIcon } color={ SemanticUIColor.TEAL }/> Select Team Members</Header>
                                                            <FinePrintMessage>Specify the organization this project will belong to, and who in that organization will have access to this project.</FinePrintMessage>
                                                        </UseIf>

                                                        { sectionState[ setupView ].inputsHaveErrors &&
                                                          ( sectionState[ setupView ].sectionHasInputsTouched || setupView === SETUP_VIEWS.TEMPLATE_SELECTION ) && (
                                                              <Segment color={ 'red' } stacked>
                                                                  <List size={ 'mini' } color={ 'red' } bulleted>
                                                                      { _.pairs( sectionState[ setupView ].sectionErrors )
                                                                         .map( ( [ key, err ] ) => {

                                                                             let errorMessage = err;

                                                                             const ListItem: FC = ( { children } ) => (
                                                                                 <List.Item><strong className={ 'text--colored-red' }>{ children }</strong></List.Item>
                                                                             );

                                                                             if( _.isArray( err ) ){

                                                                                 return err.map( ( arrayErrorItem, index ) => (
                                                                                     <ListItem key={ index }>
                                                                                         <>Row { index + 1 }:</>
                                                                                         <List bulleted>
                                                                                             { _.pairs( arrayErrorItem )
                                                                                                .map( ( [ arrayErrorKey, arrayErrorMessage ] ) => (
                                                                                                    <List.Item key={ 'arrayErrorKey' }>{ arrayErrorMessage }</List.Item>
                                                                                                ) ) }
                                                                                         </List>
                                                                                     </ListItem>
                                                                                 ) );
                                                                             }

                                                                             return <ListItem key={ key }>{ errorMessage }</ListItem>;

                                                                         } ) }
                                                                  </List>
                                                              </Segment>
                                                          ) }

                                                        <React.Fragment key={ selectedTemplate }>

                                                            { hasSelectedTemplate() && (
                                                                <Segment key={ selectedTemplate } secondary style={ { position: 'absolute', bottom: 80, width: '100%' } }>
                                                                    <Header as={ 'h6' } className={ 'text--colored-darkTeal' }>Template Selected</Header>
                                                                    <Header as={ 'h5' } className={ 'no-margin-top' }>{ getSelectedTemplate( selectedTemplate )?.name }</Header>
                                                                    <FinePrintMessage>{ getSelectedTemplate( selectedTemplate )?.description }</FinePrintMessage>
                                                                </Segment>
                                                            ) }

                                                        </React.Fragment>

                                                    </>
                                                ) }
                                            />

                                        </UseIf>

                                    </ErrorBoundary>

                                </BasicDialogContainer>

                            </Form>

                        </QueryOpDialog>

                    </> );
            } }

        </Formik>
    );
}

export {
    CreateProjectDialog,
    CreateProjectDialogDefaultProps,
    type CreateProjectDialogProps,
};
